import {
  Component,
  computed,
  inject,
  input,
  Input,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BillsService } from '../../../core/services/bills.service';
import { LocalService } from '../../../core/services/local.service';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { ReceiptData } from '../../../shared/models';
import { ObjectLiteral } from '../../../core/models/ObjectLiteral';
import dayjs from 'dayjs';
import { CommonModule } from '@angular/common';
import { ReceiptComponent } from '../../user-protected/tax-payment/receipt/receipt.component';
import { PaymentGatewayModalComponent } from '../../../shared/payment-gateway-modal/payment-gateway-modal.component';
import { takeUntil } from 'rxjs';
import { Unsub } from '../../../core/services/unsub.class';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import { resetPaymentGatewayStore } from '../../../core/store/actions/paymentGateway.actions';
import { DemandNoticeInvoiceComponent } from '../../tao/demand-notice-invoice/demand-notice-invoice.component';
import { SpecialInvoiceComponent } from '../../tao/special-invoice/special-invoice.component';

import { ConsolidatedDemandNoticeInvoiceComponent } from '../../tao/consolidated-demand-notice-invoice/consolidated-demand-notice-invoice.component';
@Component({
  standalone: true,
  selector: 'app-payment-summary-open',
  imports: [
    ConsolidatedDemandNoticeInvoiceComponent,
    CommonModule,
    ReceiptComponent,
    RouterModule,
    PaymentGatewayModalComponent,
    DemandNoticeInvoiceComponent,
    SpecialInvoiceComponent,
  ],
  templateUrl: './payment-summary-open.component.html',
  styleUrl: './payment-summary-open.component.css',
})
export class PaymentSummaryOpenComponent extends Unsub {
  activatedRoute = inject(ActivatedRoute);
  store = inject(Store<AppState>);
  billService = inject(BillsService);
  localService = inject(LocalService);
  redirectQueryparams = injectQueryParams('redirect');
  isAnonymous = injectQueryParams('isAnonymous');

  isAReceipt = computed(() => {
    return this.billQueryResponse.data()?.bill?.payStatusId ?? 1;
  });

  isInvoice = computed(() => {
    return this.billQueryResponse.data()?.bill?.payStatusId === 1;
  });

  @Input() amount;
  @Input() status;
  billReference = input<string>();
  triggerRefetch = input();

  ref = signal('');

  isLoading = computed(() => {
    return (
      (this.billQueryResponse.fetchStatus() === 'fetching' &&
        this.billQueryResponse.isPending()) ||
      (this.billTypeQueryResponse.fetchStatus() === 'fetching' &&
        this.billTypeQueryResponse.isPending())
    );
  });

  billType = computed(() => {
    return this.billTypeQueryResponse.data() || 'Unknown';
  });

  billTypeQueryResponse = injectQuery(() => {
    return {
      queryKey: ['get-bill-type', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billReference] = queryKey;
        const billRef = ref || billReference;
        return this.billService.getBillType({
          billReference: billRef,
        });
      },
      enabled: !!this.ref() || !!this.billReference(),
      retry: 1,
    };
  });

  demandNoticebillQueryResponse = injectQuery(() => {
    return {
      queryKey: ['demand-notice-single-bill', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getDemandNoticeBill({
          billReference: ref || billRef,
        });
      },
      enabled:
        (!!this.ref() || !!this.billReference()) && this.billType() === 'DN',
      retry: 1,
    };
  });

  specialbillQueryResponse = injectQuery(() => {
    return {
      queryKey: ['special-single-bill', this.ref(), this.billReference()],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getSpecialBill({
          billReference: ref || billRef,
        });
      },
      enabled:
        (!!this.ref() || !!this.billReference()) &&
        this.billType() === 'Special',
      retry: 1,
    };
  });

  billQueryResponse = injectQuery(() => {
    return {
      queryKey: [
        'single-bill',
        this.ref(),
        this.billReference(),
        this.triggerRefetch(),
      ],
      queryFn: ({ queryKey }) => {
        const [, ref, billRef] = queryKey;
        return this.billService.getBill({
          billReference: ref || billRef,
        });
      },
      enabled:
        (!!this.ref() || !!this.billReference()) &&
        this.billType() === 'Normal',
      retry: 1,
    };
  });

  receiptData = computed<ReceiptData>(() => {
    const bill = this.billQueryResponse.data() as ObjectLiteral;
    const userData = this.billQueryResponse.data()?.userData?.user;
    const userAddress =
      this.billQueryResponse.data()?.userData?.userRegistration?.addressLine1;
    return {
      ...bill,
      billTo: {
        name: `${userData?.firstName} ${userData?.lastName}`,
        address: userAddress || '',
        email: `${userData?.email}`,
      },
      billFrom: {
        name: 'Kaduna State Internal Revenue Service',
        address: 'Kaduna State',
      },
      items: (bill?.bill?.billDetails || []).map((item) => ({
        id: item?.id,
        amount: item?.amount || 0,
        rate: item?.amount || 0,
        description: item?.description || '',
        quantity: 1,
        narration: item?.narration || '',
      })),
      totalAmount:
        (bill?.bill?.billDetails || []).reduce((a, b) => a + b.amount, 0) || 0,
      userData: this.billQueryResponse.data()?.userData || {},
    } as ReceiptData;
  });

  get issuanceDate() {
    return dayjs().format('MMM DD, YYYY');
  }
  get expiryDate() {
    return dayjs().add(3, 'months').format('MMM DD, YYYY');
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((paramsMap) => {
        this.ref.set(
          paramsMap.get('billRef') ||
            paramsMap.get('billReference') ||
            paramsMap.get('ref'),
        );
      });
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.store.dispatch(resetPaymentGatewayStore());
  }
}

// add-demand-notice-invoice-apis
